import {
  DisclosureCollapse,
  DisclosureExpand,
  DisclosureNext,
  DisclosurePrevious,
} from '@sas/react-svg';
import {
  Children,
  createElement,
  forwardRef,
  Fragment,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { BaseScrollableProps } from '.';
import { usePreviousValue } from '../../../hooks/usePreviousValue';
import { useRefArray } from '../../../hooks/useRefArray';
import { useResizeObserverRef } from '../../../hooks/useResizeObserver';
import { useScroll } from '../../../hooks/useScroll';
import { useIsRTL } from '../../../locale';
import { createUseTextLocalizer } from '../../../locale/createUseTextLocalizer';
import { setRef } from '../../../util/ref-util';
import { postfixTestId } from '../../../util/testId';
import { Button, ButtonVariant } from '../../Button';
import bundle from '../NovaComponents-Scrollable-gui-icu.properties';
import { useDragHoverOnScrollButton } from './hooks/useDragHoverOnScrollButton';

import cx from 'classnames';
import './../Scrollable.css';
import cn from '../Scrollable.css.json';

const useTextLocalizer = createUseTextLocalizer(bundle);

declare namespace JSX {
  interface IntrinsicElements {
    ListTag: any;
  }
}

export const BaseScrollable = forwardRef<HTMLDivElement, BaseScrollableProps>(
  function BaseScrollable({ variant = 'standard', ...props }, ref) {
    let ListTag;
    const renderUsingListTags = props.renderUsingListTags ?? true;
    if (!renderUsingListTags) {
      ListTag = 'div' as JSX.IntrinsicElements['ListTag'];
    } else {
      ListTag = (props.allowOrderedList ? `ol` : `ul`) as JSX.IntrinsicElements['ListTag'];
    }

    const contentRef = useRef<HTMLElement | null>(null);
    const contentListDivRef = useRef<HTMLDivElement | null>(null);
    const outerContainerRef = useRef<HTMLDivElement | null>(null);
    const localize = useTextLocalizer();
    const { refs: itemRefs, createSetRef: createItemRef } = useRefArray<HTMLElement>();
    const itemCount = Children.count(props.children);
    const [backEnabled, setBackEnabled] = useState(false);
    const [nextEnabled, setNextEnabled] = useState(true);
    const [isOverflowing, setIsOverflowing] = useState<boolean>(false);
    const [focused, setFocused] = useState<boolean>(false);
    const [
      fnResizeSensorContainer,
      getResizeSensorContainer,
    ] = useResizeObserverRef<HTMLDivElement>(checkForOverflow);
    const buttonVariant = variant === 'standard' ? ButtonVariant.Icon : ButtonVariant.PrimaryIcon;
    const isRTL = useIsRTL();
    const {
      scrollTo,
      elementIsFullyVisible,
      elementBackIsClipped,
      elementNextIsClipped,
    } = useScroll(contentRef, props.orientation);

    const hideScrollButtons = props.hideScrollButtons ?? false;

    const prevCurrentIndex = usePreviousValue(props.currentIndex);

    // Function to retrieve ul's & contentListDiv's margin and padding values
    const getDimensionalProperties = () => {
      const marginStyleKeys = ['marginTop', 'marginBottom', 'marginLeft', 'marginRight'];
      const paddingStyleKeys = ['paddingTop', 'paddingBottom', 'paddingLeft', 'paddingRight'];

      const fallBackStyles = [...marginStyleKeys, ...paddingStyleKeys].reduce((acc, styleName) => {
        return {
          ...acc,
          [styleName]: 0,
        };
      }, {} as Record<string, number>);

      const contentListDiv = getComputedStyle(contentListDivRef.current!) ?? {};
      const ul = getComputedStyle(contentRef.current!) ?? {};

      const contentListDivStyles = Object.entries(contentListDiv)
        .filter((entry) => [...marginStyleKeys, ...paddingStyleKeys].includes(entry[0]))
        .reduce(
          (acc, styleEntry) => ({
            ...acc,
            [styleEntry[0]]: styleEntry[1] ? parseFloat(styleEntry[1]) : 0,
          }),
          fallBackStyles
        );

      const ulStyles = Object.entries(ul)
        .filter((entry) => [...marginStyleKeys].includes(entry[0]))
        .reduce(
          (acc, styleEntry) => ({
            ...acc,
            [styleEntry[0]]: styleEntry[1] ? parseFloat(styleEntry[1]) : 0,
          }),
          fallBackStyles
        );

      const totalMarginHorizontal =
        contentListDivStyles.marginLeft +
        contentListDivStyles.marginRight +
        ulStyles.marginLeft +
        ulStyles.marginRight;

      const totalMarginVertical =
        contentListDivStyles.marginTop +
        contentListDivStyles.marginBottom +
        ulStyles.marginTop +
        ulStyles.marginBottom;

      const totalPaddingHorizontal =
        contentListDivStyles.paddingLeft + contentListDivStyles.paddingRight;
      const totalPaddingVertical =
        contentListDivStyles.paddingTop + contentListDivStyles.paddingBottom;

      return {
        totalMarginHorizontal,
        totalMarginVertical,
        totalPaddingHorizontal,
        totalPaddingVertical,
      };
    };

    const refDeltaSizes = useRef<number[]>([]);

    function checkForOverflow() {
      if (props.disableOverflowCheck) return;

      const currentItemRefs = props.itemRefs ? (props.itemRefs as HTMLElement[]) : itemRefs;
      const currentItemCount = props.itemRefs ? currentItemRefs.length : itemCount;
      const containerRef = getResizeSensorContainer();
      if (!contentRef.current || !containerRef || !currentItemRefs) return;
      let nextIsOverflowing = false;
      if (
        (props.orientation === 'vertical' && containerRef.clientHeight === 0) ||
        (props.orientation !== 'vertical' && containerRef.clientWidth === 0)
      ) {
        return;
      }

      let deltaSize = 0;

      if (currentItemCount <= 1) {
        nextIsOverflowing = false;
      } else if (props.orientation === 'vertical' && contentListDivRef.current) {
        const { totalMarginVertical, totalPaddingVertical } = getDimensionalProperties();
        deltaSize =
          contentRef.current.scrollHeight +
          totalMarginVertical +
          totalPaddingVertical -
          containerRef.clientHeight;
        nextIsOverflowing =
          containerRef.clientHeight <
          contentRef.current.scrollHeight + totalMarginVertical + totalPaddingVertical;
      } else if (contentListDivRef.current) {
        const { totalMarginHorizontal, totalPaddingHorizontal } = getDimensionalProperties();
        deltaSize =
          contentRef.current.scrollWidth +
          totalMarginHorizontal +
          totalPaddingHorizontal -
          containerRef.clientWidth;
        nextIsOverflowing =
          containerRef.clientWidth <
          contentRef.current.scrollWidth + totalMarginHorizontal + totalPaddingHorizontal;
      }

      if (
        refDeltaSizes.current.length === 0 ||
        // if deltaSize is not changed, it may be caused by rerender instead of shaking
        refDeltaSizes.current[refDeltaSizes.current.length - 1] !== deltaSize
      ) {
        refDeltaSizes.current.push(deltaSize);
      }

      if (refDeltaSizes.current.length > 10) {
        refDeltaSizes.current.shift();
      }

      if (isOverflowing !== nextIsOverflowing && !isShaking(refDeltaSizes.current)) {
        setIsOverflowing(nextIsOverflowing);
        props.onOverflowChange?.(nextIsOverflowing);
      } else {
        // when get rid of nextIsOverflowing because of shaking, should reset it be isOverflowing to avoid calculation error when scrolling below
        nextIsOverflowing = isOverflowing;
      }

      // the empty <ul> element's scrollWidth may be 1px but its parent element's clientWidth is 0px in Safari,
      // which makes the navigation buttons shown in Safari.
      // So adding a additional condition to avoid wrong overflow status.

      let currentItemIndex = -1;
      // if focus is inside the list scroll to currentIndex. Else, if focused moved out of the
      // list maintain the focus at the selectedIndex.
      if (focused || (!focused && props.selectedIndex === undefined) || props.suppressScrollOnBlur)
        currentItemIndex = props.currentIndex;
      else currentItemIndex = props.selectedIndex !== undefined ? props.selectedIndex : -1;
      if (nextIsOverflowing && currentItemIndex !== -1 && currentItemRefs[currentItemIndex]) {
        let direction: 'back' | 'next' | null = null;
        // deleted items ref's are stored as null in currentItemRefs
        const currentRefs = currentItemRefs.filter((e) => e !== null);
        if (
          currentItemIndex === currentRefs.length - 1 ||
          (prevCurrentIndex && prevCurrentIndex - currentItemIndex === -1)
        )
          direction = props.orientation !== 'vertical' && isRTL ? 'back' : 'next';
        if (
          currentItemIndex === 0 ||
          (prevCurrentIndex && prevCurrentIndex - currentItemIndex === 1)
        )
          direction = props.orientation !== 'vertical' && isRTL ? 'next' : 'back';

        if (!props.hideScrollButtons) {
          scrollTo(currentItemRefs[currentItemIndex] as HTMLElement, direction);
          if (
            currentItemRefs[currentItemCount - 1] &&
            elementNextIsClipped(currentItemRefs[currentItemCount - 1] as HTMLElement) !==
              nextEnabled
          ) {
            setNextEnabled(
              elementNextIsClipped(currentItemRefs[currentItemCount - 1] as HTMLElement)
            );
          } else if (
            currentItemRefs[0] &&
            elementBackIsClipped(currentItemRefs[0] as HTMLElement) !== backEnabled
          ) {
            setBackEnabled(elementBackIsClipped(currentItemRefs[0] as HTMLElement));
          }
        } else {
          setTimeout(
            () => scrollTo(currentItemRefs[currentItemIndex] as HTMLElement, direction),
            0
          );
        }
      }
    }

    useLayoutEffect(checkForOverflow);

    const setOuterContainerRef = useCallback(
      (el: HTMLDivElement | null) => {
        outerContainerRef.current = el;
        setRef(ref, el);
      },
      [ref]
    );

    const changeSelection = useCallback(
      (direction: 'back' | 'next') => {
        const currentItemRefs = props.itemRefs ? (props.itemRefs as HTMLElement[]) : itemRefs,
          currentItemCount = props.itemRefs ? currentItemRefs.length : itemCount;
        if (!contentRef.current || !currentItemCount) return;
        const itemVisibilityMap = currentItemRefs.map(
          (itemRef) => itemRef && elementIsFullyVisible(itemRef)
        );
        if (direction === 'back') {
          const firstVisible = itemVisibilityMap.indexOf(true);
          let prevPossibleIndex = props.currentIndex - 1,
            prevPossibleLastIndex = firstVisible - 1;
          for (let i = prevPossibleLastIndex; i >= 0; i--) {
            if (itemVisibilityMap[i] !== undefined) {
              prevPossibleLastIndex = i;
              break;
            }
          }
          for (let i = prevPossibleIndex; i >= 0; i--) {
            if (itemVisibilityMap[i] !== undefined) {
              prevPossibleIndex = i;
              break;
            }
          }
          props.setCurrentIndex(
            Math.max(0, firstVisible === -1 ? prevPossibleIndex : prevPossibleLastIndex)
          );
        } else {
          const lastVisible = itemVisibilityMap.lastIndexOf(true);
          const nextPossibleLastIndex = itemVisibilityMap.findIndex((_, i) => {
            return i > lastVisible && itemVisibilityMap[i] !== undefined;
          });
          let nextPossibleIndex = itemVisibilityMap.findIndex((_, i) => {
            return i > props.currentIndex && itemVisibilityMap[i] !== undefined;
          });
          nextPossibleIndex = nextPossibleIndex === -1 ? props.currentIndex : nextPossibleIndex;
          props.setCurrentIndex(
            Math.min(
              currentItemCount - 1,
              lastVisible === -1 ? nextPossibleIndex : nextPossibleLastIndex
            )
          );
        }
      },
      [elementIsFullyVisible, itemCount, itemRefs, props]
    );

    const contentClassNames = cx(
      cn.scrollContent,
      props.contentClassName,
      props.contentProps?.className,
      hideScrollButtons && cn.showScrollBar
    );
    const Tag = (!renderUsingListTags ? 'div' : 'li') as JSX.IntrinsicElements['ListTag'];
    const items = Children.map(props.children, (value, i) => (
      <Fragment key={i}>
        {i > 0 ? props.separator : null}
        <Tag
          ref={createItemRef(i)}
          className={props.itemClassName?.(i)}
          role={props.contentProps && props.contentProps.role ? 'presentation' : undefined}
          onFocus={() => {
            setFocused(true);
          }}
        >
          {value}
        </Tag>
      </Fragment>
    ));
    const attributesOfList = {
      ref: (scrollableRef: HTMLElement) => {
        contentRef.current = scrollableRef;
        setRef(props.contentRef, scrollableRef);
      },
      ...{ ...props.contentProps },
      className: contentClassNames,
      onScroll: props.onScroll,
      onScrollCapture: () => {
        if (hideScrollButtons) return;
        const currentItemRefs = props.itemRefs ? (props.itemRefs as HTMLElement[]) : itemRefs,
          currentItemCount = props.itemRefs ? currentItemRefs.length : itemCount;
        if (!contentRef.current || !currentItemCount) return;
        const firstItem = currentItemRefs[0];
        const lastItem = currentItemRefs[currentItemCount - 1];
        firstItem && setBackEnabled(elementBackIsClipped(firstItem));
        lastItem && setNextEnabled(elementNextIsClipped(lastItem));
      },
    };

    const previousBtnRef = useRef<HTMLButtonElement>(null);
    const nextBtnRef = useRef<HTMLButtonElement>(null);

    useDragHoverOnScrollButton(
      previousBtnRef,
      props.acceptDropTypes ?? [],
      useCallback(() => {
        if (backEnabled) {
          setFocused(true);
          changeSelection('back');
        }
      }, [backEnabled, changeSelection])
    );

    useDragHoverOnScrollButton(
      nextBtnRef,
      props.acceptDropTypes ?? [],
      useCallback(() => {
        if (nextEnabled) {
          setFocused(true);
          changeSelection('next');
        }
      }, [nextEnabled, changeSelection])
    );

    return (
      <div
        className={cx(
          props.className,
          props.orientation === 'vertical' ? cn.vertical : cn.horizontal
        )}
        style={{ ...props.style }}
        ref={setOuterContainerRef}
        data-testid={props.testId}
        onFocus={props.onFocus}
        role={props.role}
        aria-label={props.ariaLabel}
        data-landmark-label={props.landmarkLabel}
      >
        <div
          className={cn.scrollContainer}
          ref={fnResizeSensorContainer}
          onBlur={(e) => {
            if (
              props.selectedIndex !== undefined &&
              !getResizeSensorContainer()?.contains(e.nativeEvent.relatedTarget as Node)
            ) {
              const currentItemRefs = props.itemRefs ? (props.itemRefs as HTMLElement[]) : itemRefs;
              const currentItem = currentItemRefs[props.selectedIndex] ?? null;
              if (!props.suppressScrollOnBlur && currentItem) {
                scrollTo(currentItem);
                props.setCurrentIndex(props.selectedIndex);
              }
              setFocused(false);
            }
          }}
        >
          {!hideScrollButtons && isOverflowing && (
            <Button
              ref={previousBtnRef}
              testId={postfixTestId(props.testId, 'previousBtn')}
              className={cn.scrollButton}
              variant={buttonVariant}
              disabled={!backEnabled}
              focusable={false}
              onPress={() => {
                setFocused(true);
                changeSelection('back');
              }}
              ariaLabel={localize('back.button.aria')}
              icon={{
                icon: props.orientation === 'vertical' ? DisclosureCollapse : DisclosurePrevious,
                tooltip: '',
                ariaLabel: '',
              }}
              enableContinuousPress
            />
          )}
          <div
            className={cx(props.scrollWindowClassName, cn.fadeContainer)}
            ref={(element) => {
              contentListDivRef.current = element;
              setRef(props.scrollWindowRef, element);
            }}
            data-testid={postfixTestId(props.testId, 'scrollWindow')}
          >
            <ListTag {...attributesOfList} aria-describedby={props.contentAriaDescribedBy}>
              {items}
            </ListTag>
          </div>
          {!hideScrollButtons && isOverflowing && (
            <Button
              ref={nextBtnRef}
              testId={postfixTestId(props.testId, 'nextBtn')}
              className={cn.scrollButton}
              variant={buttonVariant}
              disabled={!nextEnabled}
              focusable={false}
              onPress={() => {
                setFocused(true);
                changeSelection('next');
              }}
              ariaLabel={localize('next.button.aria')}
              icon={{
                icon: props.orientation === 'vertical' ? DisclosureExpand : DisclosureNext,
                ariaLabel: '',
                tooltip: '',
              }}
              enableContinuousPress
            />
          )}
        </div>
      </div>
    );
  }
);

function isShaking(deltaSizes: number[]) {
  if (deltaSizes.length < 4) return false;

  const count = deltaSizes.length;

  return (
    (deltaSizes[count - 1] === deltaSizes[count - 3] &&
      deltaSizes[count - 2] === deltaSizes[count - 4]) ||
    Math.abs(deltaSizes[count - 1]) + Math.abs(deltaSizes[count - 2]) ===
      Math.abs(deltaSizes[count - 2]) + Math.abs(deltaSizes[count - 4])
  );
}
